import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Users } from "lucide-react";
import { Card } from "../ui/card";

interface LeadSelectorProps {
  selectedLeads: string[];
  onLeadsChange: (leads: string[]) => void;
}

export const LeadSelector = ({ selectedLeads, onLeadsChange }: LeadSelectorProps) => {
  const [searchQuery, setSearchQuery] = useState("");

  const { data: leads = [], isLoading } = useQuery({
    queryKey: ['newsletter-leads'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('newsletter_leads')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data;
    }
  });

  const filteredLeads = leads.filter(lead => 
    lead.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (lead.full_name && lead.full_name.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const handleSelectAll = () => {
    if (selectedLeads.length === filteredLeads.length) {
      onLeadsChange([]);
    } else {
      onLeadsChange(filteredLeads.map(lead => lead.id));
    }
  };

  const handleLeadSelect = (leadId: string) => {
    if (selectedLeads.includes(leadId)) {
      onLeadsChange(selectedLeads.filter(id => id !== leadId));
    } else {
      onLeadsChange([...selectedLeads, leadId]);
    }
  };

  if (isLoading) {
    return <div className="flex items-center justify-center p-4">Loading leads...</div>;
  }

  return (
    <Card className="p-4 space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search leads..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>
        <Button
          variant="outline"
          onClick={handleSelectAll}
          className="whitespace-nowrap flex items-center gap-2"
        >
          <Users className="h-4 w-4" />
          {selectedLeads.length === filteredLeads.length ? "Deselect All" : "Select All"}
        </Button>
      </div>

      <div className="border rounded-lg divide-y max-h-[300px] overflow-y-auto">
        {filteredLeads.map((lead) => (
          <div
            key={lead.id}
            className="flex items-center space-x-4 p-4 hover:bg-muted/50"
          >
            <Checkbox
              checked={selectedLeads.includes(lead.id)}
              onCheckedChange={() => handleLeadSelect(lead.id)}
              id={`lead-${lead.id}`}
            />
            <label
              htmlFor={`lead-${lead.id}`}
              className="flex-1 cursor-pointer"
            >
              <div className="font-medium">{lead.email}</div>
              {lead.full_name && (
                <div className="text-sm text-muted-foreground">{lead.full_name}</div>
              )}
            </label>
          </div>
        ))}
      </div>
      
      <div className="text-sm text-muted-foreground flex items-center gap-2">
        <Users className="h-4 w-4" />
        {selectedLeads.length} leads selected
      </div>
    </Card>
  );
};